import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Loader from "../../components/Molecules/Loader/Loader";
import { getValidationRunScoringConfigById } from "../../services/scoringService";
import { getActiveRunInstance } from "../../services/runInstance";
import { getAllResourceList } from "../../services/resourcesService";
import { useAuth } from "../../contexts/AuthContext";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { IconBtnEditBase } from "../../common/utils/icons";

const ViewValidationRunScoring: React.FC = () => {
  const { id: configId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("validation_run_scoring_config", "edit");
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const [isLoading, setIsLoading] = useState(false);
  const [configData, setConfigData] = useState<any>(
    location.state?.rowData || null,
  );
  const [runMap, setRunMap] = useState<Map<number, string>>(new Map());
  const [resourceMap, setResourceMap] = useState<Map<number, string>>(
    new Map(),
  );

  // ── Build lookup maps on mount ──────────────────────────────────────────
  useEffect(() => {
    getActiveRunInstance()
      .then((runs) => {
        if (Array.isArray(runs)) {
          const m = new Map<number, string>();
          runs.forEach((r: any) => {
            if (r.id != null) m.set(r.id, r.run_name);
          });
          setRunMap(m);
        }
      })
      .catch(() => {});

    getAllResourceList()
      .then((resources) => {
        if (Array.isArray(resources)) {
          const m = new Map<number, string>();
          resources.forEach((r: any) => {
            if (r.id != null) m.set(r.id, r.resource_name);
          });
          setResourceMap(m);
        }
      })
      .catch(() => {});
  }, []);

  // ── Fetch config ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (configId) {
      setIsLoading(true);
      getValidationRunScoringConfigById(Number(configId))
        .then((d) => {
          if (d) setConfigData(d);
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [configId]);

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: `Config #${configId}`,
      id: configId ? parseInt(configId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [configId]);

  const field = (label: string, value: any, md: number = 4) => (
    <Grid item xs={12} sm={6} md={md}>
      <label className="label-text">{label}</label>
      <div className="form-control">{value ?? "—"}</div>
    </Grid>
  );

  const runName = configData?.run_instance_id
    ? (runMap.get(configData.run_instance_id) ??
      `Run #${configData.run_instance_id}`)
    : null;

  const resourceName = configData?.resource_id
    ? (resourceMap.get(configData.resource_id) ??
      `Resource #${configData.resource_id}`)
    : null;

  return (
    <Box>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box className="text-box-card-white">
        <Box className="text-box-header">
          <h3>View Validation Run Scoring Config</h3>
        </Box>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
        >
          {field("Execution Group Name", runName)}
          {field("Resource Name", resourceName)}
          {canEdit && (
            <Grid
              item
              xs={12}
              sm={6}
              md
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() =>
                  navigate(`/scoring/validation-run/edit/${configId}`)
                }
              >
                <Box className="d-block">
                  <IconBtnEditBase ariaLabel="Edit Validation Run Scoring Config" />
                </Box>
              </button>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          rowSpacing={2.5}
          columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
          sx={{ mt: "20px" }}
        >
          {field("Severity", configData?.severity)}
          {field("Factor", configData?.factor)}
        </Grid>
      </Box>
    </Box>
  );
};

export default ViewValidationRunScoring;
